import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { contactInfo } from '../mockData';

export const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1605437241278-c1806d14a4d9"
          alt="Car Interior"
          className="w-full h-full object-cover scale-110 animate-slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-900/95"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.15),transparent_50%)]"></div>
      </div>

      {/* Animated Bubbles */}
      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
        <div className="bubble-animation bubble-1"></div>
        <div className="bubble-animation bubble-2"></div>
        <div className="bubble-animation bubble-3"></div>
        <div className="bubble-animation bubble-4"></div>
        <div className="bubble-animation bubble-5"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 z-20 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo with Better Contrast */}
          <div className="mb-6 sm:mb-8 animate-fade-in-up">
            <div className="inline-block p-4 sm:p-8 rounded-2xl sm:rounded-3xl bg-white shadow-2xl">
              <img
                src={contactInfo.logo}
                alt={contactInfo.businessName}
                className="h-20 sm:h-28 w-auto"
              />
            </div>
          </div>

          {/* Main Heading with Gradient */}
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-white mb-4 sm:mb-6 animate-fade-in-up animation-delay-200 drop-shadow-lg px-4">
            {contactInfo.businessName}
          </h1>

          {/* Slogan with Modern Badge */}
          <div className="inline-block mb-6 sm:mb-8 animate-fade-in-up animation-delay-400 px-4">
            <div className="px-4 sm:px-8 py-2 sm:py-4 rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-lg border border-blue-400/30">
              <p className="text-lg sm:text-2xl md:text-3xl text-blue-200 font-bold">
                {contactInfo.slogan}
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-8 sm:mb-12 max-w-3xl mx-auto animate-fade-in-up animation-delay-600 leading-relaxed px-4">
            Profesionální čištění vozidel s péčí o každý detail. Specializuji se na kompletní čištění interiérů i exteriérů a tepování sedadel.
          </p>

          {/* CTA Buttons - Modern Style */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center animate-fade-in-up animation-delay-800 px-4">
            <Button
              size="lg"
              onClick={scrollToContact}
              className="group relative bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 rounded-2xl shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 border-0 w-full sm:w-auto"
            >
              <span className="relative z-10 font-bold">Objednat službu</span>
              <div className="absolute inset-0 rounded-2xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Button>
            <a href={`tel:${contactInfo.phone}`} className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="backdrop-blur-xl bg-white/10 border-2 border-white/30 text-white hover:bg-white hover:text-gray-900 text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 rounded-2xl shadow-2xl hover:shadow-white/50 transform hover:scale-105 transition-all duration-300 w-full"
              >
                <Phone className="mr-2 sm:mr-3" size={20} />
                <span className="font-bold">{contactInfo.phone}</span>
              </Button>
            </a>
          </div>

          {/* Quick Contact Info */}
          <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-8 text-gray-300 animate-fade-in-up animation-delay-1000 px-4">
            <div className="flex items-center gap-2 justify-center">
              <Phone size={18} className="text-blue-400 flex-shrink-0" />
              <span className="text-sm sm:text-base">{contactInfo.phone}</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <Mail size={18} className="text-blue-400 flex-shrink-0" />
              <span className="text-sm sm:text-base break-all">{contactInfo.email}</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <MapPin size={18} className="text-blue-400 flex-shrink-0" />
              <span className="text-sm sm:text-base">{contactInfo.address}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};
